import { Link } from "react-router-dom";
import {
  Building2, Hotel, Factory, Briefcase, PartyPopper, Warehouse, HardHat, DoorOpen, UserCheck, Camera,
  ClipboardCheck, Landmark, Shield, ArrowRight, ChevronRight, Phone, MessageCircle,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/context/SiteContext";
import { mediaUrl, telHref, waHref } from "@/lib/api";

const ICONS = {
  building: Building2, hotel: Hotel, factory: Factory, briefcase: Briefcase, event: PartyPopper, warehouse: Warehouse,
  construction: HardHat, door: DoorOpen, vip: UserCheck, camera: Camera, consult: ClipboardCheck, industry: Landmark,
};
export const ServiceIcon = ({ name, className }) => {
  const I = ICONS[name] || Shield;
  return <I className={className} />;
};

export function Breadcrumbs({ items }) {
  return (
    <nav aria-label="Sayfa yolu" className="mb-6 flex flex-wrap items-center gap-1 text-xs text-zinc-400">
      <Link to="/" className="hover:text-white">Ana Sayfa</Link>
      {items.map((it) => (
        <span key={it.path || it.name} className="flex items-center gap-1">
          <ChevronRight className="h-3 w-3" />
          {it.path ? <Link to={it.path} className="hover:text-white">{it.name}</Link> : <span className="text-zinc-300">{it.name}</span>}
        </span>
      ))}
    </nav>
  );
}

export function PageHero({ title, subtitle, image, crumbs = [], eyebrow }) {
  return (
    <section className="grain relative overflow-hidden bg-[color:var(--ink)] text-white" data-testid="page-hero">
      {image && <img src={mediaUrl(image)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />}
      <div className="hero-shade absolute inset-0" />
      <div className="container-x relative py-14 sm:py-20">
        <Breadcrumbs items={crumbs} />
        {eyebrow && <span className="eyebrow rise">{eyebrow}</span>}
        <h1 className="rise rise-1 mt-3 max-w-3xl text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl" data-testid="page-title">
          {title}
        </h1>
        {subtitle && <p className="rise rise-2 mt-4 max-w-2xl text-base text-zinc-300 sm:text-lg">{subtitle}</p>}
      </div>
      <div className="absolute bottom-0 left-0 h-1 w-full bg-[color:var(--red)]" />
    </section>
  );
}

export function CtaBand() {
  const { settings: s } = useSite();
  return (
    <section className="bg-[color:var(--red)] text-white" data-testid="cta-band">
      <div className="container-x flex flex-col gap-6 py-12 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-2xl">
          <h2 className="text-2xl font-extrabold leading-tight sm:text-3xl">{s.cta_title}</h2>
          <p className="mt-2 text-sm text-white/85 sm:text-base">{s.cta_text}</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Link to="/teklif-al" className="inline-flex items-center justify-center gap-2 rounded-md bg-[color:var(--ink)] px-6 py-3 text-sm font-bold transition-colors hover:bg-black" data-testid="cta-quote-btn">
            Hemen Teklif Al <ArrowRight className="h-4 w-4" />
          </Link>
          <a href={telHref(s.phone)} className="inline-flex items-center justify-center gap-2 rounded-md border border-white/60 px-6 py-3 text-sm font-bold transition-colors hover:bg-white/10" data-testid="cta-call-btn">
            <Phone className="h-4 w-4" /> {s.phone}
          </a>
        </div>
      </div>
    </section>
  );
}

export function FaqList({ faqs = [], testid = "faq-list" }) {
  if (!faqs.length) return null;
  return (
    <Accordion type="single" collapsible className="divide-y divide-zinc-200 rounded-lg border border-zinc-200 bg-white" data-testid={testid}>
      {faqs.map((f, i) => (
        <AccordionItem key={f.id || f.q} value={`f${i}`} className="border-0 px-5">
          <AccordionTrigger className="text-left text-sm font-bold text-zinc-900 hover:no-underline sm:text-base" data-testid={`${testid}-q-${i}`}>
            {f.q}
          </AccordionTrigger>
          <AccordionContent className="text-sm leading-relaxed text-zinc-600">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function ServiceCard({ item, i }) {
  return (
    <Link to={`/hizmetler/${item.slug}`} className="group relative flex flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-1 hover:border-[color:var(--red)] hover:shadow-xl" data-testid={`service-card-${item.slug}`}>
      <div className="relative aspect-[16/10] overflow-hidden bg-zinc-900">
        <img src={mediaUrl(item.image)} alt={item.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        {item.icon && (
          <span className="absolute bottom-3 left-3 flex h-10 w-10 items-center justify-center rounded-md bg-[color:var(--red)] text-white">
            <ServiceIcon name={item.icon} className="h-5 w-5" />
          </span>
        )}
        {i !== undefined && <span className="absolute right-3 top-3 font-display text-xs font-bold text-white/70">{String(i + 1).padStart(2, "0")}</span>}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-base font-bold text-zinc-950 sm:text-lg">{item.title}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-zinc-600">{item.short}</p>
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[color:var(--red)]">
          Detaylı bilgi <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export function ContactAside() {
  const { settings: s } = useSite();
  return (
    <aside className="rounded-lg bg-[color:var(--ink)] p-6 text-white" data-testid="contact-aside">
      <Shield className="h-8 w-8 text-[color:var(--red)]" />
      <h3 className="mt-3 text-lg font-bold">Teklif ve bilgi için</h3>
      <p className="mt-2 text-sm text-zinc-400">İhtiyacınızı dinleyelim, size özel planlama yapalım.</p>
      <div className="mt-5 flex flex-col gap-3">
        <Link to="/teklif-al" className="btn-red" data-testid="aside-quote-btn">Teklif Al</Link>
        <a href={telHref(s.phone)} className="btn-ghost-dark" data-testid="aside-call-btn"><Phone className="h-4 w-4" /> Hemen Ara</a>
        <a href={waHref(s)} target="_blank" rel="noopener noreferrer" className="btn-ghost-dark" data-testid="aside-whatsapp-btn">
          <MessageCircle className="h-4 w-4" /> WhatsApp
        </a>
      </div>
    </aside>
  );
}
